import { memo, useMemo } from 'react'
import { differenceInCalendarDays, format } from 'date-fns'
import { MeetingIcon } from '../../../../icons/MeetingIcon'
import { AudioNote } from '@highlight/common'
import { Mic } from 'lucide-react'

export const Timestamp = memo(({ className, date }: { className?: string; date: Date | string }) => {
  const today = new Date()
  const realDate = useMemo(() => {
    if (typeof date === 'string') {
      return new Date(date)
    }
    return date
  }, [date])
  const daysAgo = differenceInCalendarDays(today, realDate)

  const formatted = daysAgo <= 7 ? format(realDate, "EEEE 'at' h:mmaaa") : format(realDate, "MMMM do 'at' h:mmaaa")
  return <span className={className}>{formatted}</span>
})

interface AudioNoteIconProps {
  size: number
  conversation?: AudioNote
}
export const AudioNoteIcon = memo(({ size, conversation }: AudioNoteIconProps) => {
  const meetingApp = conversation?.meeting?.app
  let icon = <Mic size={size} />
  if (meetingApp) {
    icon = <MeetingIcon appName={meetingApp.name} size={size} />
  }
  return icon
})
AudioNoteIcon.displayName = 'AudioNoteIcon'

export const useAudioNoteTitle = (conversation: AudioNote | null | undefined) => {
  return useMemo(() => {
    const conversationTitle = conversation?.title?.split?.('from')?.[0] ?? 'Audio Notes'

    const windowTitle = conversation?.meeting?.windowTitle ?? ''
    const windowAppName = conversation?.meeting?.appName ?? ''
    const meetingAppName = conversation?.meeting?.app?.name

    if (meetingAppName) {
      const isDefaultTitle = conversationTitle?.replace(meetingAppName, '')?.trim() === 'Audio Notes'
      if (isDefaultTitle && meetingAppName == 'Google Meet') {
        const meetTitle =
          windowTitle
            ?.split(' - ')?.[1]
            ?.trim()
            ?.replace(windowAppName, '')
            ?.replace('Camera and microphone recording', '')
            ?.trim() ?? windowTitle.replace(windowAppName, '')
        const meetRegex = /\b[a-z]{3}-[a-z]{4}-[a-z]{3}\b/i
        const hasMeetingCode = meetTitle.match(meetRegex)
        if (windowAppName.startsWith(meetTitle) || hasMeetingCode) {
          return `${meetingAppName}`
        }
        return meetTitle?.length > 0 ? meetTitle : meetingAppName
      } else if (isDefaultTitle && meetingAppName == 'Zoom') {
        return meetingAppName
      }
    }
    return conversationTitle
  }, [conversation])
}
